import React, { useState } from 'react';
import {
  Container,
  Paper,
  TextField,
  Button,
  Box,
  Typography,
  Alert,
  List,
  ListItem,
  ListItemText,
  Chip,
  CircularProgress,
  Stack,
  Divider
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'https://tispxqf3hi.execute-api.us-east-1.amazonaws.com/prod';

function PublicTransactionSearch() {
  const today = new Date().toISOString().split('T')[0];
  const [cardLastFour, setCardLastFour] = useState('');
  const [amount, setAmount] = useState('');
  const [searchDate, setSearchDate] = useState(today);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [error, setError] = useState('');
  const [searched, setSearched] = useState(false);

  const handleSearch = async () => {
    if (!cardLastFour || !amount) {
      setError('Please enter both card last 4 digits and transaction amount');
      return;
    }

    if (cardLastFour.length !== 4 || !/^\d{4}$/.test(cardLastFour)) {
      setError('Card last 4 digits must be exactly 4 numbers');
      return;
    }

    if (isNaN(amount) || parseFloat(amount) <= 0) {
      setError('Please enter a valid amount');
      return;
    }

    setLoading(true);
    setError('');
    setResults([]);
    setSearched(true);

    try {
      const response = await axios.get(`${API_URL}/api/v1/public/transactions/search`, {
        params: {
          cardLastFour,
          amount,
          date: searchDate
        }
      });

      setResults(response.data.transactions || []);
      if (response.data.transactions.length === 0) {
        setError('No transactions found matching your search criteria');
      }
    } catch (err) {
      console.error('Search error:', err);
      if (err.response?.status === 429) {
        setError('Too many requests. Please wait a moment and try again.');
      } else {
        setError('Unable to search transactions. Please try again later.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return '';
    // Parse time and format it
    const [hours, minutes] = timeStr.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    // Handle ISO strings like "2026-04-24T00:00:00.000Z" or plain "2026-04-24"
    const clean = dateStr.split('T')[0];
    const [year, month, day] = clean.split('-');
    return `${month}/${day}/${year}`;
  };

  return (
    <Container maxWidth="sm" sx={{ mt: 4, mb: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom align="center">
          Transaction Status Lookup
        </Typography>
        <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
          Search for recent transactions by entering the card's last 4 digits and amount
        </Typography>

        <Box component="form" noValidate autoComplete="off">
          <Stack spacing={3}>
            <TextField
              fullWidth
              label="Card Last 4 Digits"
              value={cardLastFour}
              onChange={(e) => setCardLastFour(e.target.value.replace(/\D/g, '').slice(0, 4))}
              onKeyPress={handleKeyPress}
              placeholder="1234"
              inputProps={{ maxLength: 4, pattern: '[0-9]*' }}
              required
            />

            <TextField
              fullWidth
              label="Transaction Amount"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="0.00"
              type="number"
              inputProps={{ step: '0.01', min: '0' }}
              required
            />

            <TextField
              fullWidth
              label="Transaction Date"
              type="date"
              value={searchDate}
              onChange={(e) => setSearchDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ max: today }}
            />

            <Button
              fullWidth
              variant="contained"
              size="large"
              onClick={handleSearch}
              disabled={loading || !cardLastFour || !amount}
              startIcon={loading ? <CircularProgress size={20} /> : <SearchIcon />}
            >
              {loading ? 'Searching...' : 'Search'}
            </Button>
          </Stack>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mt: 3 }}>
            {error}
          </Alert>
        )}

        {searched && results.length > 0 && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="h6" gutterBottom>
              Search Results
            </Typography>
            <List>
              {results.map((transaction, index) => (
                <React.Fragment key={index}>
                  {index > 0 && <Divider />}
                  <ListItem sx={{ px: 0 }}>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="subtitle1">
                            Terminal {transaction.location}
                          </Typography>
                          <Chip
                            label={transaction.status}
                            size="small"
                            color={
                              transaction.status === 'Approved' ? 'success' :
                              transaction.status === 'Do Not Honor' ? 'error' :
                              transaction.status === 'Insufficient Funds' ? 'warning' :
                              'default'
                            }
                          />
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            {formatDate(transaction.date)} at {formatTime(transaction.time)}
                          </Typography>
                          <Typography variant="body2">
                            Card ending in {transaction.cardLastFour} • ${transaction.amount}{transaction.sequence ? ` • Seq #${String(transaction.sequence).padStart(3, '0')}` : ''}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItem>
                </React.Fragment>
              ))}
            </List>
            {results.length === 10 && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block', textAlign: 'center' }}>
                Showing first 10 results
              </Typography>
            )}
          </Box>
        )}
      </Paper>
    </Container>
  );
}

export default PublicTransactionSearch;